/**
 * P&L Benchmark Comparison Widget
 * ================================
 * Overlays ATO/IBISWorld industry benchmarks on P&L line items
 * Shows where the business sits vs industry peers with traffic light indicators
 * 
 * Sources: ATO Small Business Benchmarks, IBISWorld, Industry Associations
 */

import React, { useState } from 'react';
import {
  TrendingUp, TrendingDown, DollarSign, ArrowUpRight, ArrowDownRight,
  ChevronRight, Wallet, CreditCard, Receipt, Building,
  Users, Package, Zap, BarChart3, Database, Info, ToggleLeft, ToggleRight,
  CheckCircle, AlertTriangle, XCircle
} from 'lucide-react';
import { ResponsiveContainer, PieChart as RechartsPie, Pie, Cell, Tooltip } from 'recharts';
import { Progress } from '@/components/ui/progress';
import { formatCurrency, formatPercent } from '@/utils/formatters';

// ATO Benchmark data by industry (ANZSIC-based)
const ATO_BENCHMARKS = {
  hospitality: {
    source: 'ATO Small Business Benchmarks - Cafes, Restaurants & Takeaway (ANZSIC 4511-4513)',
    lastUpdated: 'FY2023-24',
    revenue: { median: 850000, lowerQuartile: 420000, upperQuartile: 1800000 },
    cogs: { benchmark: 30, range: [26, 35], label: 'Cost of Goods Sold' },
    labor: { benchmark: 30, range: [25, 38], label: 'Wages & Salaries' },
    rent: { benchmark: 8, range: [5, 14], label: 'Rent & Occupancy' },
    marketing: { benchmark: 3, range: [1, 6], label: 'Marketing & Advertising' },
    other: { benchmark: 7, range: [4, 12], label: 'Other Operating Expenses' },
    netMargin: { benchmark: 12, range: [5, 22], label: 'Net Profit Margin' },
    grossMargin: { benchmark: 65, range: [55, 75], label: 'Gross Margin' },
  },
  retail: {
    source: 'ATO Small Business Benchmarks - Retail Trade (ANZSIC 39-43)',
    lastUpdated: 'FY2023-24',
    revenue: { median: 1200000, lowerQuartile: 580000, upperQuartile: 3500000 },
    cogs: { benchmark: 58, range: [45, 68], label: 'Cost of Goods Sold' },
    labor: { benchmark: 14, range: [10, 20], label: 'Wages & Salaries' },
    rent: { benchmark: 8, range: [5, 14], label: 'Rent & Occupancy' },
    marketing: { benchmark: 4, range: [2, 8], label: 'Marketing & Advertising' },
    other: { benchmark: 5, range: [3, 10], label: 'Other Operating Expenses' },
    netMargin: { benchmark: 8, range: [3, 15], label: 'Net Profit Margin' },
    grossMargin: { benchmark: 42, range: [32, 55], label: 'Gross Margin' },
  },
  professional: {
    source: 'ATO Small Business Benchmarks - Professional Services (ANZSIC 69-70)',
    lastUpdated: 'FY2023-24',
    revenue: { median: 650000, lowerQuartile: 280000, upperQuartile: 2200000 },
    labor: { benchmark: 55, range: [40, 65], label: 'Staff Costs' },
    rent: { benchmark: 8, range: [4, 14], label: 'Office & Utilities' },
    software: { benchmark: 5, range: [3, 10], label: 'Software & Tools' },
    marketing: { benchmark: 3, range: [1, 7], label: 'Marketing & BD' },
    other: { benchmark: 6, range: [3, 12], label: 'Other Operating' },
    netMargin: { benchmark: 20, range: [10, 35], label: 'Net Profit Margin' },
    grossMargin: { benchmark: 45, range: [30, 60], label: 'Gross Margin' },
  },
  healthcare: {
    source: 'ATO Small Business Benchmarks - Health Care & Social Assistance (ANZSIC 84-87)',
    lastUpdated: 'FY2023-24',
    revenue: { median: 720000, lowerQuartile: 350000, upperQuartile: 2500000 },
    labor: { benchmark: 45, range: [35, 58], label: 'Practitioner & Staff Costs' },
    rent: { benchmark: 10, range: [6, 16], label: 'Rent & Occupancy' },
    supplies: { benchmark: 8, range: [4, 15], label: 'Medical Supplies' },
    marketing: { benchmark: 2, range: [1, 5], label: 'Marketing' },
    other: { benchmark: 7, range: [4, 12], label: 'Other Operating' },
    netMargin: { benchmark: 22, range: [12, 35], label: 'Net Profit Margin' },
    grossMargin: { benchmark: 55, range: [40, 70], label: 'Gross Margin' },
  },
  accounting: {
    source: 'IPA Practice Management Benchmarks + ATO (ANZSIC 6932)',
    lastUpdated: 'FY2023-24',
    revenue: { median: 780000, lowerQuartile: 320000, upperQuartile: 3000000 },
    labor: { benchmark: 52, range: [42, 62], label: 'Staff Costs' },
    rent: { benchmark: 7, range: [4, 12], label: 'Office & Utilities' },
    software: { benchmark: 6, range: [3, 10], label: 'Software & Subscriptions' },
    marketing: { benchmark: 2, range: [1, 5], label: 'Marketing & BD' },
    other: { benchmark: 5, range: [3, 10], label: 'Other Operating' },
    netMargin: { benchmark: 25, range: [15, 38], label: 'Net Profit Margin' },
    grossMargin: { benchmark: 48, range: [35, 58], label: 'Gross Margin' },
  },
  legal: {
    source: 'Law Society Practice Benchmarks + ATO (ANZSIC 6931)',
    lastUpdated: 'FY2023-24',
    revenue: { median: 950000, lowerQuartile: 400000, upperQuartile: 4000000 },
    labor: { benchmark: 50, range: [40, 62], label: 'Staff Costs' },
    rent: { benchmark: 9, range: [5, 15], label: 'Office & Utilities' },
    software: { benchmark: 4, range: [2, 8], label: 'Software & Systems' },
    marketing: { benchmark: 2, range: [1, 5], label: 'Marketing & BD' },
    other: { benchmark: 6, range: [3, 12], label: 'Other Operating' },
    netMargin: { benchmark: 28, range: [15, 42], label: 'Net Profit Margin' },
    grossMargin: { benchmark: 50, range: [35, 62], label: 'Gross Margin' },
  },
  property: {
    source: 'REIA Benchmarks + ATO (ANZSIC 6720)',
    lastUpdated: 'FY2023-24',
    revenue: { median: 580000, lowerQuartile: 280000, upperQuartile: 2000000 },
    labor: { benchmark: 42, range: [32, 55], label: 'Staff Costs' },
    rent: { benchmark: 10, range: [6, 16], label: 'Office & Utilities' },
    marketing: { benchmark: 8, range: [4, 15], label: 'Marketing & Advertising' },
    other: { benchmark: 8, range: [5, 14], label: 'Other Operating' },
    netMargin: { benchmark: 18, range: [8, 30], label: 'Net Profit Margin' },
    grossMargin: { benchmark: 55, range: [40, 68], label: 'Gross Margin' },
  },
  technology: {
    source: 'IBISWorld + ATO (ANZSIC 7000)',
    lastUpdated: 'FY2023-24',
    revenue: { median: 520000, lowerQuartile: 180000, upperQuartile: 5000000 },
    labor: { benchmark: 48, range: [35, 62], label: 'Staff & Contractors' },
    rent: { benchmark: 5, range: [2, 10], label: 'Office & Infrastructure' },
    software: { benchmark: 8, range: [4, 15], label: 'Software & Hosting' },
    marketing: { benchmark: 10, range: [5, 20], label: 'Marketing & Sales' },
    other: { benchmark: 5, range: [3, 10], label: 'Other Operating' },
    netMargin: { benchmark: 15, range: [5, 35], label: 'Net Profit Margin' },
    grossMargin: { benchmark: 65, range: [50, 85], label: 'Gross Margin' },
  },
};

// Demo P&L data
const PL_DATA = {
  hospitality: {
    revenue: { mtd: 185420, target: 200000, lastMonth: 172350, ytd: 1854200 },
    expenses: {
      cogs: { amount: 51918, percent: 28, target: 28, label: 'Cost of Goods', icon: Package },
      labor: { amount: 59334, percent: 32, target: 32, label: 'Labor', icon: Users },
      rent: { amount: 18542, percent: 10, target: 10, label: 'Rent & Utilities', icon: Building },
      marketing: { amount: 5563, percent: 3, target: 4, label: 'Marketing', icon: Zap },
      other: { amount: 9271, percent: 5, target: 5, label: 'Other', icon: Receipt },
    },
    grossMargin: 72,
    netMargin: 22,
    targetNetMargin: 25,
  },
  retail: {
    revenue: { mtd: 245680, target: 260000, lastMonth: 228450, ytd: 2456800 },
    expenses: {
      cogs: { amount: 147408, percent: 60, target: 58, label: 'Cost of Goods', icon: Package },
      labor: { amount: 34395, percent: 14, target: 15, label: 'Labor', icon: Users },
      rent: { amount: 24568, percent: 10, target: 10, label: 'Rent & Utilities', icon: Building },
      marketing: { amount: 12284, percent: 5, target: 6, label: 'Marketing', icon: Zap },
      other: { amount: 7370, percent: 3, target: 3, label: 'Other', icon: Receipt },
    },
    grossMargin: 40,
    netMargin: 8,
    targetNetMargin: 10,
  },
  professional: {
    revenue: { mtd: 312500, target: 325000, lastMonth: 298750, ytd: 3125000 },
    expenses: {
      labor: { amount: 187500, percent: 60, target: 58, label: 'Staff Costs', icon: Users },
      rent: { amount: 31250, percent: 10, target: 10, label: 'Office & Utilities', icon: Building },
      software: { amount: 15625, percent: 5, target: 5, label: 'Software & Tools', icon: Zap },
      marketing: { amount: 9375, percent: 3, target: 4, label: 'Marketing', icon: Zap },
      other: { amount: 15625, percent: 5, target: 5, label: 'Other', icon: Receipt },
    },
    grossMargin: 40,
    netMargin: 17,
    targetNetMargin: 20,
  },
};

const COLORS = ['#f59e0b', '#3b82f6', '#8b5cf6', '#10b981', '#6b7280'];
const GRADIENT_COLORS = [
  { from: 'from-amber-400', to: 'to-orange-500', shadow: 'shadow-amber-500/25' },
  { from: 'from-blue-400', to: 'to-indigo-500', shadow: 'shadow-blue-500/25' },
  { from: 'from-violet-400', to: 'to-purple-500', shadow: 'shadow-violet-500/25' },
  { from: 'from-emerald-400', to: 'to-[#0066CC]', shadow: 'shadow-emerald-500/25' },
  { from: 'from-gray-400', to: 'to-slate-500', shadow: 'shadow-gray-500/25' },
];

// Get benchmark status indicator
const getBenchmarkStatus = (actual, benchmark, range, isInverse = true) => {
  // For expenses, lower is better (isInverse=true). For margins, higher is better (isInverse=false)
  if (isInverse) {
    if (actual <= range[0]) return { status: 'excellent', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle, label: 'Below benchmark' };
    if (actual <= benchmark) return { status: 'good', color: 'text-emerald-600', bg: 'bg-emerald-50/50', border: 'border-emerald-100', icon: CheckCircle, label: 'At benchmark' };
    if (actual <= range[1]) return { status: 'warning', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, label: 'Above benchmark' };
    return { status: 'critical', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, label: 'Well above benchmark' };
  } else {
    if (actual >= range[1]) return { status: 'excellent', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-200', icon: CheckCircle, label: 'Above benchmark' };
    if (actual >= benchmark) return { status: 'good', color: 'text-emerald-600', bg: 'bg-emerald-50/50', border: 'border-emerald-100', icon: CheckCircle, label: 'At benchmark' };
    if (actual >= range[0]) return { status: 'warning', color: 'text-amber-700', bg: 'bg-amber-50', border: 'border-amber-200', icon: AlertTriangle, label: 'Below benchmark' };
    return { status: 'critical', color: 'text-red-700', bg: 'bg-red-50', border: 'border-red-200', icon: XCircle, label: 'Well below benchmark' };
  }
};

// Benchmark bar component
const BenchmarkBar = ({ actual, benchmark, range, isInverse = true }) => {
  const min = Math.min(range[0], actual) - 2;
  const max = Math.max(range[1], actual) + 2;
  const totalRange = max - min;
  
  const rangeLeft = ((range[0] - min) / totalRange) * 100;
  const rangeWidth = ((range[1] - range[0]) / totalRange) * 100;
  const actualPos = ((actual - min) / totalRange) * 100;
  const benchmarkPos = ((benchmark - min) / totalRange) * 100;
  const status = getBenchmarkStatus(actual, benchmark, range, isInverse);
  
  return (
    <div className="mt-1.5">
      <div className="relative h-2.5 bg-gray-100 rounded-full overflow-visible">
        {/* Industry range band */}
        <div 
          className="absolute top-0 h-full bg-blue-100 rounded-full border border-blue-200/50"
          style={{ left: `${rangeLeft}%`, width: `${rangeWidth}%` }}
        />
        {/* Benchmark median marker */}
        <div 
          className="absolute top-0 h-full w-0.5 bg-blue-500 z-10"
          style={{ left: `${benchmarkPos}%` }}
        />
        {/* Actual value marker */}
        <div 
          className={`absolute -top-0.5 w-3.5 h-3.5 rounded-full border-2 border-white shadow-md z-20 ${
            status.status === 'excellent' || status.status === 'good' ? 'bg-emerald-500' : 
            status.status === 'warning' ? 'bg-amber-500' : 'bg-red-500'
          }`}
          style={{ left: `calc(${actualPos}% - 7px)` }}
        />
      </div>
      <div className="flex justify-between mt-0.5">
        <span className="text-[9px] text-gray-400">{range[0]}%</span>
        <span className="text-[9px] text-blue-500 font-medium">Median: {benchmark}%</span>
        <span className="text-[9px] text-gray-400">{range[1]}%</span>
      </div>
    </div>
  );
};

const PLBenchmarkWidget = ({
  industry = 'hospitality',
  className = '',
  compact = false,
  onViewDetails
}) => {
  const [showBenchmarks, setShowBenchmarks] = useState(true);
  const data = PL_DATA[industry] || PL_DATA.hospitality;
  const benchmarks = ATO_BENCHMARKS[industry] || ATO_BENCHMARKS.hospitality;
  
  const totalExpenses = Object.values(data.expenses).reduce((sum, e) => sum + e.amount, 0);
  const netProfit = data.revenue.mtd - totalExpenses;
  const revenueProgress = (data.revenue.mtd / data.revenue.target) * 100;
  const revenueVsLastMonth = ((data.revenue.mtd - data.revenue.lastMonth) / data.revenue.lastMonth * 100).toFixed(1);
  
  const netMarginStatus = getBenchmarkStatus(data.netMargin, benchmarks.netMargin.benchmark, benchmarks.netMargin.range, false);
  const NetMarginIcon = netMarginStatus.icon;
  
  const expenseChartData = Object.entries(data.expenses).map(([key, value], idx) => ({
    name: value.label,
    value: value.amount,
    percent: value.percent,
    color: COLORS[idx]
  }));

  if (compact) {
    return (
      <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-5 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] hover:-translate-y-0.5 ${className}`} data-testid="pl-benchmark-compact">
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-400 to-[#0066CC] flex items-center justify-center shadow-lg shadow-emerald-500/20">
                <DollarSign className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 tracking-tight">P&L vs Industry</h4>
            </div>
            <div className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${netMarginStatus.bg} ${netMarginStatus.color} border ${netMarginStatus.border}`}>
              <NetMarginIcon className="w-3 h-3" />
              {data.netMargin}% margin
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-50/80 to-blue-50/50 border border-emerald-100">
              <p className="text-xl font-bold text-emerald-700">{formatCurrency(data.revenue.mtd)}</p>
              <p className="text-[10px] text-emerald-600 font-medium">Revenue MTD</p>
            </div>
            <div className={`p-3 rounded-2xl border ${netMarginStatus.bg} ${netMarginStatus.border}`}>
              <p className={`text-xl font-bold ${netMarginStatus.color}`}>{formatCurrency(netProfit)}</p>
              <p className={`text-[10px] ${netMarginStatus.color} font-medium`}>Net Profit</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] ${className}`} data-testid="pl-benchmark-widget">
      <div className="absolute -top-20 -right-20 w-56 h-56 bg-gradient-to-br from-emerald-100/50 to-teal-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative">
        {/* Header with Benchmark Toggle */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-emerald-400 to-[#0066CC] flex items-center justify-center shadow-lg shadow-emerald-500/25">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <div>
              <div className="flex items-center gap-2.5">
                <h3 className="font-semibold text-gray-900 tracking-tight">Real-time P&L</h3>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">MTD</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowBenchmarks(!showBenchmarks)}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-full transition-all ${
                showBenchmarks 
                  ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-sm' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50 border border-transparent'
              }`}
              data-testid="benchmark-toggle"
            >
              <BarChart3 className="w-3.5 h-3.5" />
              vs Industry
              {showBenchmarks ? <ToggleRight className="w-4 h-4 text-blue-600" /> : <ToggleLeft className="w-4 h-4" />}
            </button>
            <button onClick={onViewDetails} className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors">
              Full P&L <ChevronRight className="w-3 h-3 ml-1" />
            </button>
          </div>
        </div>

        {/* Benchmark Source Badge */}
        {showBenchmarks && (
          <div className="flex items-center gap-2 mb-4 p-2.5 rounded-xl bg-blue-50/60 border border-blue-100">
            <Database className="w-3.5 h-3.5 text-blue-500 flex-shrink-0" />
            <p className="text-[10px] text-blue-600 leading-tight">
              <span className="font-semibold">Benchmarks:</span> {benchmarks.source} ({benchmarks.lastUpdated})
            </p>
          </div>
        )}

        {/* Revenue Section */}
        <div className="p-4 rounded-2xl bg-gradient-to-br from-emerald-50/80 to-blue-50/50 border border-emerald-100 mb-5">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-emerald-800">Revenue</span>
            <div className="flex items-center gap-1.5 text-emerald-600 text-xs font-medium">
              {parseFloat(revenueVsLastMonth) >= 0 ? (
                <><ArrowUpRight className="w-3.5 h-3.5" /> +{revenueVsLastMonth}%</>
              ) : (
                <><ArrowDownRight className="w-3.5 h-3.5" /> {revenueVsLastMonth}%</>
              )}
              <span className="text-emerald-500">vs LM</span>
            </div>
          </div>
          <div className="flex items-end justify-between mb-3">
            <span className="text-3xl font-bold text-emerald-800">{formatCurrency(data.revenue.mtd)}</span>
            <span className="text-sm text-emerald-600">of {formatCurrency(data.revenue.target)} target</span>
          </div>
          <Progress value={revenueProgress} className="h-2.5 mb-2" />
          <p className="text-xs text-emerald-600 font-medium">{revenueProgress.toFixed(0)}% to monthly target</p>
          {showBenchmarks && benchmarks.revenue && (
            <div className="mt-2 pt-2 border-t border-emerald-100">
              <p className="text-[10px] text-emerald-600">
                Industry median: {formatCurrency(benchmarks.revenue.median)}/yr 
                (Range: {formatCurrency(benchmarks.revenue.lowerQuartile)} - {formatCurrency(benchmarks.revenue.upperQuartile)})
              </p>
            </div>
          )}
        </div>

        {/* Expense Breakdown with Benchmark Comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
          <div className="h-36 flex items-center justify-center">
            <ResponsiveContainer width="100%" height={144}>
              <RechartsPie>
                <Pie data={expenseChartData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" label={false} strokeWidth={2} stroke="white">
                  {expenseChartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => formatCurrency(value)} contentStyle={{ fontSize: '11px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
          <div className="space-y-2 flex flex-col justify-center">
            {expenseChartData.map((expense, idx) => (
              <div key={`${expense?.id ?? expense?.name ?? expense?.title ?? 'expense'}-${idx}`} className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: expense.color }} />
                <span className="text-xs text-gray-600 flex-1 truncate">{expense.name}</span>
                <span className="text-xs font-bold text-gray-900">{expense.percent}%</span>
              </div>
            ))}
          </div>
        </div>

        {/* Expense Details with Benchmark Overlay */}
        <div className="space-y-2 mb-5">
          {Object.entries(data.expenses).map(([key, expense], idx) => {
            const Icon = expense.icon;
            const gradientStyle = GRADIENT_COLORS[idx % GRADIENT_COLORS.length];
            const benchmark = benchmarks[key];
            const benchmarkInfo = benchmark ? getBenchmarkStatus(expense.percent, benchmark.benchmark, benchmark.range, true) : null;
            const BenchmarkIcon = benchmarkInfo?.icon;
            
            return (
              <div key={key} className="p-3 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50 transition-all hover:shadow-md hover:-translate-y-0.5">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${gradientStyle.from} ${gradientStyle.to} flex items-center justify-center shadow-lg ${gradientStyle.shadow} flex-shrink-0`}>
                    <Icon className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-900 truncate">{expense.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">{formatCurrency(expense.amount)}</span>
                        {showBenchmarks && benchmarkInfo && (
                          <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${benchmarkInfo.bg} ${benchmarkInfo.color} border ${benchmarkInfo.border}`}>
                            <BenchmarkIcon className="w-2.5 h-2.5" />
                            {expense.percent}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-xs text-gray-500">
                        {expense.percent}% of revenue
                      </span>
                      {!showBenchmarks && expense.percent > expense.target && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700 border border-red-200">Over budget</span>
                      )}
                    </div>
                    {/* Benchmark comparison bar */}
                    {showBenchmarks && benchmark && (
                      <BenchmarkBar
                        actual={expense.percent}
                        benchmark={benchmark.benchmark}
                        range={benchmark.range}
                        isInverse={true}
                      />
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Net Profit Summary with Benchmark */}
        <div className={`p-4 rounded-2xl border ${netProfit > 0 ? 'bg-gradient-to-br from-blue-50/80 to-indigo-50/50 border-blue-100' : 'bg-gradient-to-br from-red-50/80 to-rose-50/50 border-red-100'}`}>
          <div className="flex items-center justify-between">
            <div>
              <span className={`text-sm font-medium ${netProfit > 0 ? 'text-blue-800' : 'text-red-800'}`}>Net Profit</span>
              <div className="flex items-center gap-3 mt-1">
                <span className={`text-3xl font-bold ${netProfit > 0 ? 'text-blue-800' : 'text-red-800'}`}>
                  {formatCurrency(netProfit)}
                </span>
                <div className="flex items-center gap-1.5">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${netProfit > 0 ? 'bg-blue-200/80 text-blue-800' : 'bg-red-200/80 text-red-800'}`}>
                    {data.netMargin}% margin
                  </span>
                  {showBenchmarks && (
                    <span className={`inline-flex items-center gap-0.5 px-2 py-1 rounded-full text-[10px] font-bold ${netMarginStatus.bg} ${netMarginStatus.color} border ${netMarginStatus.border}`}>
                      <NetMarginIcon className="w-3 h-3" />
                      {netMarginStatus.label}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-right">
              {showBenchmarks ? (
                <>
                  <p className="text-xs text-gray-500 font-medium">Industry Benchmark</p>
                  <p className={`text-2xl font-bold ${data.netMargin >= benchmarks.netMargin.benchmark ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {benchmarks.netMargin.benchmark}%
                  </p>
                  <p className="text-[10px] text-gray-500">Range: {benchmarks.netMargin.range[0]}-{benchmarks.netMargin.range[1]}%</p>
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-500 font-medium">Target Margin</p>
                  <p className={`text-2xl font-bold ${data.netMargin >= data.targetNetMargin ? 'text-emerald-600' : 'text-amber-600'}`}>
                    {data.targetNetMargin}%
                  </p>
                </>
              )}
            </div>
          </div>
          {/* Net Margin Benchmark Bar */}
          {showBenchmarks && (
            <BenchmarkBar
              actual={data.netMargin}
              benchmark={benchmarks.netMargin.benchmark}
              range={benchmarks.netMargin.range}
              isInverse={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default PLBenchmarkWidget;
